import { RefObject, useEffect, useState } from 'react'

interface UseInViewOptions {
  threshold?: number | number[]
  rootMargin?: string
  /** Stop observing once the element has entered the viewport once. Default true. */
  once?: boolean
  /**
   * CSS selector for the nearest scrollable ANCESTOR to observe against
   * (resolved with `el.closest()` at observe time; no match → viewport).
   *
   * Why this exists: with the default `root: null`, `rootMargin` expands only
   * the VIEWPORT rect — the intersection is still clipped by every
   * `overflow: auto` ancestor, so a lookahead margin is inert for a sentinel
   * inside a scrollable rail (measured 2026-08-24: the directory floor's
   * 1600px lookahead never fired inside the mobile results drawer — 6.3%
   * mobile exposure vs 22.7% desktop). Passing the rail itself as root makes
   * the margin expand the rail's rect, which is what "prefetch N px before
   * the user scrolls there" actually means inside a nested scroller.
   */
  rootSelector?: string
}

/**
 * Returns true once the referenced element intersects the viewport. Honors
 * prefers-reduced-motion by treating it as immediately visible.
 */
export function useInView(
  ref: RefObject<Element | null>,
  {
    threshold = 0.2,
    rootMargin,
    once = true,
    rootSelector
  }: UseInViewOptions = {}
): boolean {
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el || visible) return

    if (
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      setVisible(true)
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (!entry) return
        if (entry.isIntersecting) {
          setVisible(true)
          if (once) observer.disconnect()
        } else if (!once) {
          setVisible(false)
        }
      },
      {
        threshold,
        rootMargin,
        root: rootSelector ? el.closest(rootSelector) : null
      }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [ref, visible, threshold, rootMargin, once, rootSelector])

  return visible
}

export default useInView
